import compose from 'lodash/fp/compose';
import { formatPrice } from '@catalogo/core-helpers/price';
import {
  addPickupOption,
  getBestFreightOptions,
  excludesLasaOption,
  getWarningMessage,
  formattedPrice,
} from './freight';

export const getDeliveryTypeKey = (modalityType, id) => {
  const fmtId = id?.toUpperCase();
  const fmtModalityType = modalityType?.toUpperCase();

  if (fmtId?.includes('LOCKER')) {
    return 'LOCKER';
  }

  return fmtModalityType;
};

export const getLasaOfferTexts = (lasaOptions, price) => {
  const { expressBDLasa, pickupBDLasa } = getBusinessDaysFromOptions({ lasaOptions });

  const { hasTwoServices, shipFromStore, pickupStore } = fmtDeadlineData({
    expressBDLasa,
    pickupBDLasa,
  });
  const fmtedPrice = fmtPrice(price);

  if (hasTwoServices) {
    return {
      deadline: { bold: 'receba ou retire', normalWeight: ' mais rápido*' },
      disclaimer: {
        normalWeight: ' e retire na loja sem pagar frete ou receba em casa mais rápido!',
        price: fmtedPrice,
      },
    };
  }

  if (pickupStore >= 0) {
    return {
      deadline: { bold: 'retire rápido', normalWeight: '*' },
      disclaimer: { normalWeight: ' e retire na loja sem pagar frete!', price: fmtedPrice },
    };
  }

  if (shipFromStore >= 0) {
    return {
      deadline: { bold: 'receba hoje', normalWeight: '*' },
      disclaimer: { normalWeight: ' e receba em casa mais rápido!', price: fmtedPrice },
    };
  }

  return {
    deadline: { bold: '', normalWeight: '' },
    disclaimer: { normalWeight: '', price: '' },
  };
};

export const getBusinessDaysFromText = option => {
  if (option?.eta?.text?.includes('amanhã')) {
    return 1;
  }

  if (option?.eta?.text?.includes('hoje')) {
    return 0;
  }

  return option?.eta?.businessDays ?? Infinity;
};

export const getBusinessDaysFromOptions = ({ options, lasaOptions }) => {
  const express = findOptionByKeyValue(options, 'modalityType', 'RECEBER');
  const pickup = findOptionByKeyValue(options, 'modalityType', 'RETIRAR');
  const expressLasa = findOptionByKeyValue(lasaOptions, 'modalityType', 'RECEBER');
  const pickupLasa = findOptionByKeyValue(lasaOptions, 'modalityType', 'RETIRAR');

  const expressBD = getBusinessDaysFromText(express);
  const pickupBD = getBusinessDaysFromText(pickup);
  const expressBDLasa = getBusinessDaysFromText(expressLasa);
  const pickupBDLasa = getBusinessDaysFromText(pickupLasa);

  return { expressBD, pickupBD, expressBDLasa, pickupBDLasa };
};

export const shouldShowLasaOffer = (options, lasaOptions) => {
  const { expressBD, pickupBD, expressBDLasa, pickupBDLasa } = getBusinessDaysFromOptions({ options, lasaOptions });
  return expressBD > expressBDLasa || pickupBD > pickupBDLasa;
};

export const shouldShowChronometer = (id, details, eta) => {
  const expressOption = id.toUpperCase().includes('EXPRESS');
  const hasClosingTime = details?.shipstoreSalesClosingTime;
  const today = eta?.businessDays === 0 && eta?.hours === 3;

  return !!(today && expressOption && hasClosingTime);
};

export const fmtDateRange = (text, modalityType, id, isPreSale) => {
  const typeText = getDeliveryTypeKey(modalityType, id);
  const typeIcon = typeText.includes('LOCKER') ? 'RETIRAR' : typeText;

  const fmtText = fmtTextWithBold(text);
  return { fmtText: isPreSale ? `${fmtText}*` : fmtText, modalityTypeIcon: typeIcon };
};

export const fmtOption = (option, isPreSale) => {
  const text = option?.eta?.text;
  const id = option?.optionId || option?.type;
  const modalityType = option?.modalityType;

  const { fmtText, modalityTypeIcon } = fmtDateRange(text, modalityType, id, isPreSale);
  const price = option?.price;
  const listPrice = option?.priceWithoutPromotion;
  const details = option?.products?.[0]?.details;
  const eta = option?.eta;

  return {
    id,
    details,
    eta,
    default: option?.default,
    price: formattedPrice(price),
    listPrice: formattedPrice(listPrice),
    fmtText,
    modalityType,
    modalityTypeIcon,
    showChronometer: shouldShowChronometer(id, details, eta),
  };
};

export const fmtFreightOptions = freightOptions => {
  if (freightOptions.length <= 1) {
    return freightOptions;
  }

  return compose(addPickupOption(freightOptions), getBestFreightOptions, excludesLasaOption)(freightOptions);
};

export const fmtFreight = (freight, freightLasa, isPreSale) => {
  const options = freight?.options || [];
  const products = options[0]?.products || [];
  const lasaOptions = freightLasa?.options || [];
  const filteredOptions = fmtFreightOptions(options).map(option => fmtOption(option, isPreSale));
  const hasO2O = filteredOptions.some(({ modalityType }) => modalityType === 'RETIRAR');
  const showLasaOffer = shouldShowLasaOffer(filteredOptions, lasaOptions);
  const firstOptionReceive = options.find(({ modalityType }) => modalityType === 'RECEBER');
  const sellerStoreId = hasO2O ? firstOptionReceive?.warehouses?.[0] : null;
  const hasFreightIncentive = products.some(
    ({ freightIncentive }) => freightIncentive?.code === 'MORE_UNIT_FOR_CHEAPER_FREIGHT'
  );

  return {
    options: filteredOptions,
    cep: freight?.cep,
    hasMoreOptions: filteredOptions.length < options.length,
    hasO2O,
    showLasaOffer,
    sellerStoreId,
    hasFreightIncentive,
  };
};

export const fmtFreightData = ({ data, dataLasa, currentSku, product, error }) => {
  const preSale = { active: product?.isPreSale, launchingAt: product?.launchingAt };
  const freight = fmtFreight(data?.freight, dataLasa?.freight, preSale.active);
  const freightError = error || data?.freight?.error;
  const unified = currentSku?.offers?.result?.[0]?.isUnified;
  const international = currentSku?.offers?.result?.[0]?.currency === 'USD';
  const warningMessage = getWarningMessage(data);

  return { freight, freightError, preSale, unified, international, warningMessage };
};

export const fmtDeadlineData = ({ expressBDLasa, pickupBDLasa }) => {
  const shipFromStore = expressBDLasa >= 0 && expressBDLasa !== Infinity ? expressBDLasa : undefined;
  const pickupStore = pickupBDLasa >= 0 && pickupBDLasa !== Infinity ? pickupBDLasa : undefined;
  const hasTwoServices = shipFromStore >= 0 && pickupStore >= 0;

  return { hasTwoServices, shipFromStore, pickupStore };
};

export const fmtPrice = price => {
  if (!price) {
    return '';
  }

  return `R$ ${formatPrice(price)}`;
};

export const fmtTextWithBold = text => {
  const scenarios = ['receba em 3 horas', 'receba até', 'receba entre', 'receba', 'retire na loja', 'retire no locker'];
  const current = scenarios.find(scenario => text?.includes(scenario));

  return text?.replace(current, `<strong>${current}</strong>`);
};

export const toogleClick = (click, setClick) => () => setClick(!click);

export const findOptionByKeyValue = (options, field, value) => options?.find(option => option?.[field] === value);
